/**
 * "إصدار الإنذار" - issue a warning letter straight from an approved QC violation
 * (owner's requirement, 2026-11).
 *
 * Uses the published WARNING_LETTER document template and the ordinary
 * Documents issue route, with `source_violation_id`, so the warning lands in the
 * employee's Documents tab and is linked to the violation (one active warning
 * per violation). The fields come pre-filled from the violation; HR picks the
 * level, seeing how many warnings the employee already has. Only HR Manager,
 * HR Admin and Super Admin see the button; the server checks again.
 *
 * A warning whose violation was withdrawn is cancelled by the server and shown
 * here as cancelled; if the violation is approved again HR may issue a new one.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { FileWarning, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import WarningHistoryPanel from '@/components/warnings/WarningHistoryPanel';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ISSUER_ROLES = ['hr_manager', 'hr_admin', 'super_admin'];
// An English section must stay English: Arabic typed into a text field needs an English version.
const ARABIC_TEXT = /[\u0600-\u06FF]/;
const TEXT_TYPES = ['text', 'textarea'];

const fieldLabel = (field, isRTL) => (isRTL ? field.label_ar || field.label_en : field.label_en || field.label_ar) || field.key;

const errorText = (detail, isRTL, fallback) => {
  if (detail?.missing_fields?.length) {
    const labels = detail.missing_fields.map((f) => (isRTL ? f.label_ar : f.label_en) || f.key || f.field).join(', ');
    return `${isRTL ? 'حقول مطلوبة: ' : 'Required fields: '}${labels}`;
  }
  return detail?.error || fallback;
};

export const IssueWarningButton = ({ violation, isRTL, onIssued }) => {
  const { token, hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [template, setTemplate] = useState(null);
  const [draft, setDraft] = useState(null);
  const [values, setValues] = useState({});
  const [language, setLanguage] = useState('ar_en');
  const [issuedRef, setIssuedRef] = useState(null);

  if (!hasRole(...ISSUER_ROLES)) return null;

  const cancelled = !issuedRef && violation.warning_status === 'cancelled' && violation.warning_reference_no;
  const reference = issuedRef || (!cancelled && violation.warning_reference_no);
  if (reference) {
    return (
      <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-800" data-testid={`warning-issued-${violation.id}`}>
        <FileWarning className="h-3 w-3" />
        {isRTL ? 'تم إصدار الإنذار' : 'Warning issued'} · {reference}
      </Badge>
    );
  }
  const cancelledBadge = cancelled ? (
    <Badge variant="outline" className="gap-1 border-slate-300 bg-slate-50 text-slate-600 line-through decoration-slate-400" data-testid={`warning-cancelled-${violation.id}`}>
      <FileWarning className="h-3 w-3" />
      {isRTL ? 'إنذار ملغى' : 'Warning cancelled'} · {violation.warning_reference_no}
    </Badge>
  ) : null;
  if (violation.status !== 'approved') return cancelledBadge;

  const headers = { Authorization: `Bearer ${token}` };

  const openDialog = async () => {
    setOpen(true); setLoading(true); setError(''); setTemplate(null); setDraft(null);
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API_URL}/qc-violations/${violation.id}/warning-draft`, { headers }),
        axios.get(`${API_URL}/document-templates/by-type/WARNING_LETTER?status=published`, { headers }),
      ]);
      setDraft(d.data);
      setTemplate(t.data);
      setValues({ ...(d.data.manual_payload || {}) });
    } catch (e) {
      setError(errorText(e.response?.data?.detail, isRTL,
        isRTL ? 'تعذّر تجهيز نموذج الإنذار. تأكد من وجود قالب إنذار منشور.' : 'Could not prepare the warning. Check that a warning template is published.'));
    } finally {
      setLoading(false);
    }
  };

  const fields = template?.manual_fields || [];
  const needsEnglish = (field) => language !== 'ar' && TEXT_TYPES.includes(field.field_type || 'text')
    && ARABIC_TEXT.test(String(values[field.key] ?? ''));
  const missing = fields.filter((f) => (f.required && !String(values[f.key] ?? '').trim())
    || (needsEnglish(f) && (!String(values[`${f.key}_en`] ?? '').trim() || ARABIC_TEXT.test(values[`${f.key}_en`]))));

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/documents/issue/${violation.employee_id}`, {
        document_type_code: 'WARNING_LETTER',
        language,
        manual_payload: values,
        source_violation_id: violation.id,
      }, { headers });
      setIssuedRef(res.data.reference_no);
      setOpen(false);
      toast.success(isRTL ? `تم إصدار الإنذار: ${res.data.reference_no}` : `Warning issued: ${res.data.reference_no}`);
      if (res.data.employee_updates?.pending_update_id) {
        toast.warning(isRTL ? 'الخصم من الراتب بانتظار موافقة مدير الموارد البشرية' : 'The salary deduction is waiting for HR Manager approval');
      }
      onIssued?.(res.data);
    } catch (e) {
      setError(errorText(e.response?.data?.detail, isRTL, isRTL ? 'تعذّر إصدار الإنذار' : 'The warning could not be issued'));
    } finally {
      setBusy(false);
    }
  };

  const renderField = (field) => {
    const id = `warning-${violation.id}-${field.key}`;
    const value = values[field.key] ?? '';
    const set = (v) => setValues((prev) => ({ ...prev, [field.key]: v }));
    if (field.field_type === 'select') {
      return (
        <Select value={value || undefined} onValueChange={set}>
          <SelectTrigger id={id} data-testid={id}><SelectValue placeholder={isRTL ? 'اختر' : 'Choose'} /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => (
              <SelectItem key={o.value} value={o.value}>{(isRTL ? o.label_ar : o.label_en) || o.value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    const dir = field.english ? 'ltr' : undefined;
    if (field.field_type === 'textarea') {
      return <Textarea id={id} data-testid={id} dir={dir} rows={3} value={value} onChange={(e) => set(e.target.value)} />;
    }
    const type = field.field_type === 'date' ? 'date' : ['number', 'currency'].includes(field.field_type) ? 'number' : 'text';
    return <Input id={id} data-testid={id} dir={dir} type={type} value={value} onChange={(e) => set(e.target.value)} />;
  };

  return <>
    {cancelledBadge}
    <Button size="sm" variant="outline" onClick={openDialog} data-testid={`issue-warning-${violation.id}`}>
      <FileWarning className="h-4 w-4 me-1" />{isRTL ? 'إصدار الإنذار' : 'Issue warning'}
    </Button>
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent translate="no" className="max-w-lg max-h-[90vh] overflow-y-auto notranslate" data-testid={`issue-warning-dialog-${violation.id}`}>
        <DialogHeader>
          <DialogTitle>{isRTL ? 'إصدار الإنذار' : 'Issue warning'}</DialogTitle>
          <DialogDescription>
            {isRTL
              ? 'يُصدر الإنذار من قالب الإنذار المعتمد، ويُربط بهذه المخالفة، ويُضاف إلى مستندات الموظف.'
              : "Issued from the published warning template, linked to this violation and added to the employee's documents."}
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" />{isRTL ? 'جارٍ التحميل…' : 'Loading…'}</p>}

        {draft && template && (
          <div className="space-y-4">
            <WarningHistoryPanel employeeId={violation.employee_id} token={token} isRTL={isRTL}
              testid={`warning-previous-${violation.id}`}
              levelOptions={(fields.find((f) => f.key === 'warning_level') || {}).options || []}
              selectedLevel={values.warning_level}
              onPickLevel={(v) => setValues((cur) => ({ ...cur, warning_level: v }))}
              values={values}
              onChange={(k, v) => setValues((cur) => { const next = { ...cur }; if (v === undefined) delete next[k]; else next[k] = v; return next; })} />
            <div className="space-y-2">
              <Label>{isRTL ? 'لغة الإنذار' : 'Language'}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid={`warning-language-${violation.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ar">{isRTL ? 'العربية فقط' : 'Arabic only'}</SelectItem>
                  <SelectItem value="en">{isRTL ? 'الإنجليزية فقط' : 'English only'}</SelectItem>
                  <SelectItem value="ar_en">{isRTL ? 'ثنائي اللغة' : 'Bilingual'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {fields.map((field) => (
              <div className="space-y-2" key={field.key}>
                <Label htmlFor={`warning-${violation.id}-${field.key}`}>{fieldLabel(field, isRTL)}{field.required ? ' *' : ''}</Label>
                {renderField(field)}
                {needsEnglish(field) && (
                  <div className="space-y-1 ps-3 border-s-2 border-blue-200">
                    <Label htmlFor={`warning-${violation.id}-${field.key}_en`} className="text-xs text-blue-700">
                      {isRTL ? `${field.label_ar || field.key} (بالإنجليزية) *` : `${field.label_en || field.key} (English) *`}
                    </Label>
                    {renderField({ ...field, key: `${field.key}_en`, english: true })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {error && <p role="alert" className="text-sm text-red-700" data-testid={`issue-warning-error-${violation.id}`}>{error}</p>}

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button
            disabled={busy || loading || !template || missing.length > 0}
            onClick={submit}
            data-testid={`issue-warning-confirm-${violation.id}`}
          >
            {busy ? (isRTL ? 'جارٍ الإصدار…' : 'Issuing…') : (isRTL ? 'إصدار' : 'Issue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
};

export default IssueWarningButton;
